import { useNavigate, useRouter } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import Avatar from "~/components/ui/Avatar";
import { useVariantPossibilities } from "~/sdk/useVariantPossiblities";
import type { Product } from "@decocms/apps-commerce/types";
import QuantityAvailable from "~/components/product/QuantityAvailable";
import { sanitizeStoreUrl } from "~/utils/constants";

interface Props {
  product: Product;
  onSkuChange: (skuId: string) => void;
}

function pathnameOf(url: string | undefined): string | undefined {
  return url?.split("?")[0];
}

function skuIdOf(url: string | undefined): string | undefined {
  if (!url) return undefined;
  const query = url.split("?")[1];
  return query
    ? new URLSearchParams(query).get("skuId") ?? undefined
    : undefined;
}

function VariantSelector({ product, onSkuChange }: Props) {
  const { isVariantOf } = product;
  const hasVariant = isVariantOf?.hasVariant ?? [];
  const possibilities = useVariantPossibilities(hasVariant, product);
  const navigate = useNavigate();
  const router = useRouter();

  // Spinner do clique cross-produto (navegação de verdade). A seleção
  // same-path (replaceState) não precisa de loading local nenhum: o
  // `product` recebido já é o produto efetivo (ver `Details`), então
  // `possibilities` já reflete a seleção atual sem round-trip nenhum.
  const [loadingHref, setLoadingHref] = useState<string | undefined>(
    undefined,
  );

  const handleVariantClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, href: string) => {
      e.preventDefault();
      // Mesmo path (só troca ?skuId=): já temos todos os dados de variante
      // em `hasVariant`, não precisa de navegação nenhuma — só avisa o pai
      // (Details) qual SKU foi selecionado, pra ele reconstruir o produto
      // efetivo usado no resto da página (preço, galeria, carrinho etc.).
      if (pathnameOf(href) === window.location.pathname) {
        // O replaceState acima é interceptado pelo history do TanStack
        // Router (monkeypatch em createBrowserHistory), então mesmo essa
        // chamada "crua" dispara o scroll-restoration dele — que zera o
        // scroll pra qualquer URL (skuId) nunca visitada antes. Suprime só
        // esse ciclo com a mesma flag que navigate({ resetScroll: false })
        // usaria, sem passar pelo navigate() de verdade (evita o overhead
        // de router-matching que o replaceState existe pra evitar).
        router.resetNextScroll = false;
        window.history.replaceState(null, "", href);
        const skuId = skuIdOf(href);
        if (skuId) onSkuChange(skuId);
        return;
      }
      // Path diferente: produto de verdade diferente, precisa navegar.
      setLoadingHref(href);
      navigate({ to: href });
    },
    [navigate, router, onSkuChange],
  );

  return (
    <>
      <ul className="flex flex-col gap-8 items-center lg:items-start">
        {Object.keys(possibilities)
          .filter((name) => name === "Cor" || name === "Tamanho")
          .map((name) => {
            const isColor = name === "Cor";
            const isSize = name === "Tamanho";
            const selectedColor = isColor
              ? Object.entries(possibilities["Cor"] || {}).find(([, value]) =>
                value?.includes("AVAILABLE::SELECTED::")
              )?.[0]
              : null;

            return (
              <li key={name}>
                {/* O Sizebay (provador virtual, tag já ativa no GTM) procura
                   um elemento `div#productSizeSelector` de verdade — não
                   basta o id, o script deles usa `document.querySelector`
                   com seletor composto `div#productSizeSelector`, que exige
                   a tag também. Insere os botões dele ("ENCONTRE SEU
                   TAMANHO"/"TABELA DE MEDIDAS") logo depois desta div. Sem
                   esse elemento, o script carrega mas não insere nada — sem
                   erro nenhum, achado ao vivo em produção (2026-08-26). */}
                <div
                  id={isSize ? "productSizeSelector" : undefined}
                  className="flex flex-col gap-2 items-start lg:items-start"
                >
                <span className="font-['Poppins',Helvetica,Arial,sans-serif] text-xs font-normal uppercase text-[#333333] py-2.5 uppercase">
                  {isColor ? `Cor: ${selectedColor}` : name}
                </span>
                <ul className="flex flex-wrap justify-center gap-3 lg:justify-start">
                  {Object.entries(possibilities[name])
                    .sort(([valueA], [valueB]) => {
                      const isNumberA = !isNaN(Number(valueA));
                      const isNumberB = !isNaN(Number(valueB));
                      // only sorting if the values are numbers because string sizes (P, M, G) should not be sorted
                      if (isNumberA && isNumberB) {
                        return Number(valueA) - Number(valueB);
                      } else {
                        return 0;
                      }
                    })
                    .map(([value, link]) => {
                      const href = sanitizeStoreUrl(
                        link?.split("::").slice(-1)[0],
                      );
                      const isSelected =
                        link?.split("::")[1] === "SELECTED";
                      // `link` é undefined quando a combinação cor+tamanho
                      // não existe como SKU (ex: só a cor Bege tem grade
                      // P/M/G, Rosa-3 só existe em P) — tratar como
                      // indisponível, igual ao caso explícito de estoque
                      // zerado, senão o botão fica preso sem estilo de
                      // desabilitado e o spinner abaixo dispara por engano.
                      const isNotAvailable = !link ||
                        link.includes("NOT_AVAILABLE");
                      // Sem o `Boolean(href)`, uma combinação inexistente
                      // (href undefined) e o estado inicial de `loadingHref`
                      // (também undefined) davam `undefined === undefined`
                      // = true, prendendo o botão num spinner permanente
                      // sem nenhum clique ter acontecido.
                      const isLoading = Boolean(href) &&
                        loadingHref === href;

                      return (
                        <li className="relative" key={value}>
                          <button
                            type="button"
                            onClick={isNotAvailable || !href
                              ? undefined
                              : (e) => handleVariantClick(e, href)}
                            aria-disabled={isNotAvailable}
                            aria-current={isSelected ? "true" : undefined}
                            title={value}
                            className={isNotAvailable
                              ? "pointer-events-none"
                              : undefined}
                          >
                            {isSize && isSelected && (
                              <QuantityAvailable
                                product={product}
                                link={href ?? ""}
                                productVariants={hasVariant}
                                isSelected={isSelected}
                              />
                            )}

                            <Avatar
                              content={value}
                              showImage={isColor}
                              productVariants={hasVariant}
                              link={href}
                              mainImage={product.image
                                ? product.image[0].url
                                : undefined}
                              isSelected={isSelected}
                              variant={isNotAvailable
                                ? "disabled"
                                : "default"}
                              size="lg"
                            />
                            {isLoading && (
                              <span className="loading loading-spinner loading-xs absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                            )}
                          </button>
                        </li>
                      );
                    })}
                </ul>
                </div>
              </li>
            );
          })}
      </ul>
    </>
  );
}
export default VariantSelector;
